// src/pages/kpis/KpiMtbfPage.jsx
import React, { useEffect, useMemo, useState } from "react";

import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useIsMobile } from "../../hooks/useIsMobile";

import PeriodFilter from "../../components/Kpis/PeriodFilter";

import { db } from "../../config/firebase";
import { collection, getDocs, limit, query, Timestamp, where } from "firebase/firestore";

function tsToDate(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts === "object" && typeof ts.toDate === "function") return ts.toDate();
  if (typeof ts.seconds === "number") return new Date(ts.seconds * 1000);
  return null;
}


function fmt(n, casas = 2) {
  if (n == null || Number.isNaN(n)) return "-";
  return Number(n).toFixed(casas);
}

function hoursBetween(a, b) {
  if (!a || !b) return null;
  const da = a instanceof Date ? a : a.toDate ? a.toDate() : new Date(a);
  const dbb = b instanceof Date ? b : b.toDate ? b.toDate() : new Date(b);
  const ms = dbb.getTime() - da.getTime();
  if (Number.isNaN(ms)) return null;
  return ms / 36e5;
}

export default function KpiMtbfPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario } = useAuth();
  const isMobile = useIsMobile(900);

  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [periodo, setPeriodo] = useState({ preset: "30D", start: null, end: null });

  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");

  const [resumo, setResumo] = useState({
    falhas: 0,
    horasPeriodo: null,
    maquinasChave: 0,
    totalHorasFrota: null,
    mtbfHoras: null,
  });
  const [topMaquinas, setTopMaquinas] = useState([]);

  const canLoad = useMemo(() => !!usuario?.grupoId, [usuario]);

  async function carregar() {
    if (!canLoad) return;
    if (!periodo?.start || !periodo?.end) return;

    setCarregando(true);
    setErro("");

    try {
      const ref = collection(db, "ordensServico");
      const q = query(ref, where("grupoId", "==", usuario.grupoId), limit(5000));

      const snap = await getDocs(q);
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Carrega máquinas-chave para calcular MTBF Global (frota) apenas delas
      const maqRef = collection(db, "maquinas");
      const qMaq = query(maqRef, where("grupoId", "==", usuario.grupoId), where("maquinaChave", "==", true), limit(5000));
      const maqSnap = await getDocs(qMaq);
      const maquinasChave = maqSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const chaveIds = new Set(maquinasChave.map((m) => String(m.id)));
      const chaveCodigos = new Set(maquinasChave.map((m) => String(m.codigo || "").trim()).filter(Boolean));
      const chaveNomes = new Set(maquinasChave.map((m) => String(m.nome || "").trim().toLowerCase()).filter(Boolean));

      const isOsDeMaquinaChave = (os) => {
        const id = os?.maquinaId || os?.maquina?.id || os?.maquinaRefId || null;
        const cod = os?.maquinaCodigo || os?.maquinaCod || os?.codigoMaquina || null;
        const nome = os?.maquinaNome || os?.maquinaNomeSnapshot || null;

        if (id && chaveIds.has(String(id))) return true;
        if (cod && chaveCodigos.has(String(cod).trim())) return true;
        if (nome && chaveNomes.has(String(nome).trim().toLowerCase())) return true;
        return false;
      };


      const startMs = periodo.start.getTime();
      const endMs = periodo.end.getTime();
      const lista = all
        .filter((os) => {
          const dc = tsToDate(os.criadoEm);
          if (!dc) return false;
          const ms = dc.getTime();
          return ms >= startMs && ms <= endMs;
        })
        .sort((a, b) => (tsToDate(b.criadoEm)?.getTime() || 0) - (tsToDate(a.criadoEm)?.getTime() || 0));

      // Considera “falha” quando OS indica máquina parada
      const falhas = lista.filter((os) =>
        String(os.maquinaParou || "").toLowerCase() === "sim" && isOsDeMaquinaChave(os)
      );

      const horasPeriodo = hoursBetween(periodo.start, periodo.end);
      const totalHorasFrota = maquinasChave.length ? horasPeriodo * maquinasChave.length : 0;
      const mtbfHoras = falhas.length ? totalHorasFrota / falhas.length : null;

      // Top máquinas por falhas
      const map = new Map();
      for (const os of falhas) {
        const key = os.maquinaNome || os.maquinaId || "(Sem máquina)";
        map.set(key, (map.get(key) || 0) + 1);
      }
      const top = Array.from(map.entries())
        .map(([maquina, totalFalhas]) => ({ maquina, totalFalhas }))
        .sort((a, b) => b.totalFalhas - a.totalFalhas)
        .slice(0, 10);

      setResumo({
        falhas: falhas.length,
        horasPeriodo,
        maquinasChave: maquinasChave.length,
        totalHorasFrota,
        mtbfHoras,
      });
      setTopMaquinas(top);
    } catch (e) {
      console.error(e);
      setErro("Erro ao carregar MTBF. Verifique índices/consultas do Firestore.");
      setResumo({ falhas: 0, horasPeriodo: null, maquinasChave: 0, totalHorasFrota: null, mtbfHoras: null });
      setTopMaquinas([]);
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    if (periodo?.start && periodo?.end) {
      carregar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario?.grupoId, periodo?.start, periodo?.end]);

  const conteudo = (
    <section style={{ width: "100%" }}>
      <div className="pc-card" style={{ width: "100%" }}>
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>MTBF</h3>
            <p>Tempo médio entre falhas (base: OS com “Máquina parou = Sim”).</p>
          </div>
        </header>

        <PeriodFilter value={periodo} onApply={(v) => setPeriodo(v)} />

        {erro && (
          <div className="pc-alert pc-alert-error" style={{ marginBottom: 12 }}>
            {erro}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 12 }}>
          <div className="pc-card" style={{ padding: 12 }}>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Falhas (OS com máquina parada)</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{resumo.falhas}</div>
          </div>
          <div className="pc-card" style={{ padding: 12 }}>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Horas do período (base)</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{fmt(resumo.horasPeriodo, 1)}</div>
          </div>
          <div className="pc-card" style={{ padding: 12 }}>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Máquinas-chave</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{resumo.maquinasChave}</div>
          </div>
          <div className="pc-card" style={{ padding: 12 }}>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Horas da frota (máquinas-chave)</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{fmt(resumo.totalHorasFrota, 1)}</div>
          </div>
          <div className="pc-card" style={{ padding: 12 }}>
            <div style={{ fontSize: 12, opacity: 0.8 }}>MTBF Global (horas)</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{fmt(resumo.mtbfHoras, 2)}</div>
          </div>
        </div>

        <div className="pc-card" style={{ padding: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Top máquinas (por falhas)</div>
          {carregando ? (
            <div>Carregando...</div>
          ) : topMaquinas.length ? (
            <div style={{ overflowX: "auto" }}>
              <table className="pc-table" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Máquina</th>
                    <th style={{ textAlign: "right" }}>Falhas</th>
                  </tr>
                </thead>
                <tbody>
                  {topMaquinas.map((r) => (
                    <tr key={r.maquina}>
                      <td>{r.maquina}</td>
                      <td style={{ textAlign: "right" }}>{r.totalFalhas}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ opacity: 0.8 }}>Sem dados no período.</div>
          )}
        </div>
      </div>
    </section>
  );

  return isMobile ? (
    <LayoutMobile
      tema={tema}
      alterarTema={alterarTema}
      titulo="KPIs — MTBF"
      sidebarAberta={sidebarAberta}
      onToggleSidebar={() => setSidebarAberta((prev) => !prev)}
    >
      {conteudo}
    </LayoutMobile>
  ) : (
    <LayoutDesktop tema={tema} titulo="KPIs" subtitulo="MTBF">
      {conteudo}
    </LayoutDesktop>
  );
}